import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { usePermissions } from "@/contexts/PermissionsContext";
import { SectionHeader, StatusBadge, EmptyState, KpiCard } from "@/components/Shared";
import { fmtCur, fmtDate } from "@/lib/format";
import DataTable from "@/components/DataTable";
import PdfPreviewModal from "@/components/PdfPreviewModal";
import SendEmailDialog from "@/components/SendEmailDialog";
import HelpHint from "@/components/HelpHint";
import BulkImportButton from "@/components/BulkImportButton";
import { Plus, X, Search, Pencil, Trash2, Send, FileDown, FileSpreadsheet, Eye, Briefcase, Receipt, FileText, Wallet, CreditCard, Mail } from "lucide-react";
import { toast } from "sonner";
import ConfirmDialog from "@/components/ConfirmDialog";
import { useBulkSelect, BulkCheckbox, BulkActionBar } from "@/components/BulkSelect";
import FirmFilter from "@/components/FirmFilter";

const TABS = [
  { key: "uebersicht", label: "Übersicht" },
  { key: "kunden", label: "Kunden" },
  { key: "auftraege", label: "Aufträge" },
  { key: "rechnungen", label: "Rechnungen" },
  { key: "gutschriften", label: "Gutschriften" },
  { key: "angebote", label: "Angebote" },
  { key: "zahlungen", label: "Zahlungen" },
];

export default function Finanzen() {
  const [sp, setSp] = useSearchParams();
  const [tab, setTab] = useState(sp.get("tab") || "uebersicht");

  function changeTab(k) {
    setTab(k);
    sp.set("tab", k);
    setSp(sp, { replace: true });
  }
  useEffect(() => {
    const t = sp.get("tab");
    if (t && t !== tab) setTab(t);
  }, [sp]);  // eslint-disable-line
  return (
    <div className="p-6 md:p-8" data-testid="finanzen-page">
      <SectionHeader title="Finanzen" subtitle="Kunden, Aufträge, Rechnungen, Zahlungen" />

      <HelpHint title="Wie funktioniert das Finanzen-Modul?" className="mb-4" testid="help-finanzen">
        <p>Komplette Finanzverwaltung mit <b>Kunden, Aufträgen, Rechnungen, Gutschriften, Angeboten und Zahlungen</b> — austria-konform mit UID, Mahnwesen, PDF-Vorlagen.</p>
        <ul className="list-disc list-inside space-y-0.5">
          <li><b>Übersicht</b>: KPIs (Offen / Überfällig / Umsatz Monat) auf einen Blick.</li>
          <li><b>Kunden</b>: Stammdaten + UID + Excel-Export im Kodlogy-Format.</li>
          <li><b>Aufträge</b>: Frachtaufträge mit Be-/Entlade-Adressen — werden später automatisch zur Rechnung.</li>
          <li><b>Rechnungen</b>: Nummern-Kreis fortlaufend (RE-2026-…), automatischer Status (offen → bezahlt / überfällig), PDF-Vorschau + E-Mail-Versand mit einem Klick.</li>
          <li><b>Gutschriften / Angebote</b> haben eigene Nummern-Kreise (GU- / AN-).</li>
          <li><b>Zahlungen</b>: Bank-Eingänge zuordnen (manuell oder per CSV-Import) → setzt Rechnungs-Status automatisch auf bezahlt.</li>
        </ul>
      </HelpHint>

      <div className="border-b border-zinc-800 mb-6">
        <div className="flex gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => changeTab(t.key)} data-testid={`fin-tab-${t.key}`}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                tab === t.key ? "text-zinc-50 border-blue-600" : "text-zinc-400 border-transparent hover:text-zinc-200"
              }`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {tab === "uebersicht" && <FinanzenOverview />}
      {tab === "kunden" && <Kunden />}
      {tab === "auftraege" && <Auftraege />}
      {tab === "rechnungen" && <Rechnungen docType="rechnung" title="Rechnungen" prefix="RE" />}
      {tab === "gutschriften" && <Rechnungen docType="gutschrift" title="Gutschriften" prefix="GU" />}
      {tab === "angebote" && <Rechnungen docType="angebot" title="Angebote" prefix="AN" />}
      {tab === "zahlungen" && <Zahlungen />}
    </div>
  );
}

/* ---------- Übersicht ---------- */
function FinanzenOverview() {
  const [stats, setStats] = useState(null);
  const [customers, setCustomers] = useState(0);
  const [orders, setOrders] = useState(0);
  useEffect(() => {
    api.get("/invoices/stats").then(({data}) => setStats(data));
    api.get("/customers").then(({data}) => setCustomers(data.length));
    api.get("/orders").then(({data}) => setOrders(data.length));
  }, []);
  if (!stats) return <div className="text-zinc-500 text-sm">Lade...</div>;
  const fmt = (n) => `€ ${(n||0).toLocaleString("de-AT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <KpiCard label="Umsatz gesamt" value={fmt(stats.total)} hint="brutto, alle Rechnungen" icon={Receipt} tone="default" testid="fin-kpi-total"/>
      <KpiCard label="Bezahlt" value={fmt(stats.paid)} hint="eingegangen" icon={Wallet} tone="success" testid="fin-kpi-paid"/>
      <KpiCard label="Offen" value={fmt(stats.open)} hint="noch nicht beglichen" icon={CreditCard} tone={stats.open > 0 ? "warning" : "default"} testid="fin-kpi-open"/>
      <KpiCard label="Überfällig" value={fmt(stats.overdue)} hint="über Fälligkeitsdatum" icon={CreditCard} tone={stats.overdue > 0 ? "critical" : "default"} testid="fin-kpi-overdue"/>
      <KpiCard label="Kunden" value={customers} hint="aktive Geschäftspartner" icon={Briefcase} testid="fin-kpi-customers"/>
      <KpiCard label="Aufträge" value={orders} hint="erfasst" icon={FileText} testid="fin-kpi-orders"/>
    </div>
  );
}

/* ---------- Kunden ---------- */
const EMPTY_CUSTOMER = { company_name: "", customer_number: "", uid_number: "", eori_number: "", commercial_register_number: "", contact_person: "", phone: "", mobile_phone: "", email: "", address: "", plz: "", city: "", country: "AT", notes: "" };
function Kunden() {
  const { can } = usePermissions();
  const canCreate = can("finanzen", "create");
  const canEdit = can("finanzen", "edit");
  const canDelete = can("finanzen", "delete");
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_CUSTOMER);

  // Phase 5Z.25 — bulk-select + confirm
  const bulk = useBulkSelect(items);
  const [confirmState, setConfirmState] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);

  async function load() {
    const { data } = await api.get("/customers", { params: { q } });
    setItems(data);
  }
  useEffect(() => { load(); }, [q]);

  async function save() {
    try {
      if (editing) await api.put(`/customers/${editing.id}`, form);
      else await api.post("/customers", form);
      toast.success(editing ? "Kunde aktualisiert" : "Kunde angelegt");
      setShowForm(false); setEditing(null); setForm(EMPTY_CUSTOMER); load();
    } catch { toast.error("Fehler"); }
  }
  function remove(c) { setConfirmState({ type: "single", customer: c }); }
  async function confirmRemove() {
    if (!confirmState) return;
    setConfirmBusy(true);
    try {
      if (confirmState.type === "single") {
        await api.delete(`/customers/${confirmState.customer.id}`);
        toast.success("Kunde gelöscht");
      } else {
        const ids = bulk.selectedIds;
        const results = await Promise.allSettled(ids.map((id) => api.delete(`/customers/${id}`)));
        const ok = results.filter((r) => r.status === "fulfilled").length;
        const fail = results.length - ok;
        if (ok) toast.success(`${ok} Kunde${ok === 1 ? "" : "n"} gelöscht`);
        if (fail) toast.error(`${fail} fehlgeschlagen (möglicherweise mit Rechnungen verknüpft)`);
        bulk.clear();
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen"); }
    finally { setConfirmBusy(false); setConfirmState(null); }
  }

  return (
    <div>
      <div className="flex justify-end mb-4 gap-2">
        {canCreate && (
          <BulkImportButton
            entity="customers"
            templateUrl="/imports/customers/template"
            uploadUrl="/imports/customers"
            templateFileName="Kunden_Vorlage.xlsx"
            label="Bulk-Import"
            testid="bulk-import-customers"
            onDone={load}
          />
        )}
        {canCreate && (
          <button data-testid="add-customer-btn" onClick={() => { setEditing(null); setForm(EMPTY_CUSTOMER); setShowForm(true); }}
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-md">
            <Plus className="w-4 h-4"/> Kunde
          </button>
        )}
        <button
          data-testid="export-kundenliste-btn"
          onClick={async () => {
            try {
              const res = await api.get("/exports/kundenliste-kodlogy", { responseType: "blob" });
              const url = URL.createObjectURL(res.data);
              const a = document.createElement("a");
              a.href = url;
              a.download = `Kundenliste_${new Date().toISOString().slice(0,10)}.xlsx`;
              document.body.appendChild(a); a.click(); a.remove();
              URL.revokeObjectURL(url);
              toast.success("Kundenliste heruntergeladen");
            } catch (e) {
              toast.error("Export fehlgeschlagen");
            }
          }}
          className="inline-flex items-center gap-2 bg-emerald-600/15 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-600/25 text-sm font-medium px-4 py-2 rounded-md transition-colors"
        >
          <FileSpreadsheet className="w-4 h-4" /> Excel-Export
        </button>
      </div>
      <BulkActionBar
        count={bulk.selectedCount}
        onClear={bulk.clear}
        onDelete={() => canDelete && setConfirmState({ type: "bulk", count: bulk.selectedCount })}
        deleteLabel={`${bulk.selectedCount} löschen`}
        testid="kunden-bulk-bar"
      />
      <div className="bg-zinc-900 border border-zinc-800 rounded-md">
        <div className="p-4 border-b border-zinc-800">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-zinc-500" />
            <input placeholder="Kdnr., Firma, UID, Kontakt, Ort..." value={q} onChange={(e)=>setQ(e.target.value)} className="w-full pl-9 pr-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
          </div>
        </div>
        {items.length === 0 ? <EmptyState title="Keine Kunden" /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
                <th className="px-4 py-3 text-left w-8">
                  <BulkCheckbox checked={bulk.allSelected} indeterminate={bulk.someSelected} onChange={bulk.toggleAll} disabled={!canDelete} testid="kunden-select-all" />
                </th>
                <th className="px-4 py-3 text-left">Nr.</th>
                <th className="px-4 py-3 text-left">Firma</th><th className="px-4 py-3 text-left">UID</th>
                <th className="px-4 py-3 text-left">Kontakt</th><th className="px-4 py-3 text-left">E-Mail</th>
                <th className="px-4 py-3 text-left">Ort</th><th className="px-4 py-3 text-left">Land</th>
                <th className="px-4 py-3 text-right">Aktionen</th>
              </tr></thead>
              <tbody>{items.map(c => (
                <tr key={c.id} className="border-b border-zinc-800 hover:bg-zinc-800/40">
                  <td className="px-4 py-2.5 w-8"><BulkCheckbox checked={bulk.isSelected(c.id)} onChange={() => bulk.toggle(c.id)} disabled={!canDelete} testid={`kunden-select-${c.id}`} /></td>
                  <td className="px-4 py-2.5 font-mono text-xs text-amber-300">{c.customer_number||"—"}</td>
                  <td className="px-4 py-2.5 text-zinc-100 font-medium">{c.company_name}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-zinc-400">{c.uid_number||"—"}</td>
                  <td className="px-4 py-2.5 text-zinc-300">{c.contact_person||"—"}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-zinc-400">{c.email||"—"}</td>
                  <td className="px-4 py-2.5 text-zinc-300">{c.plz} {c.city}</td>
                  <td className="px-4 py-2.5 text-zinc-400 text-xs">{c.country||"—"}</td>
                  <td className="px-4 py-2.5 text-right">
                    {canEdit && <button onClick={()=>{setEditing(c); setForm({...EMPTY_CUSTOMER,...c}); setShowForm(true);}} className="p-1.5 text-zinc-400 hover:text-blue-400"><Pencil className="w-3.5 h-3.5"/></button>}
                    {canDelete && <button onClick={()=>remove(c)} className="p-1.5 text-zinc-400 hover:text-red-400 ml-1"><Trash2 className="w-3.5 h-3.5"/></button>}
                    {!canEdit && !canDelete && <span className="text-zinc-600 text-xs">—</span>}
                  </td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <ModalForm title={editing ? "Kunde bearbeiten" : "Neuer Kunde"} onClose={()=>setShowForm(false)} onSave={save} saveTestId="customer-save-btn">
          {[
            ["company_name","Firmenname *"],["customer_number","Kundennummer"],
            ["uid_number","UID-Nummer"],["eori_number","EORI-Nummer"],
            ["commercial_register_number","Firmenbuchnummer"],["contact_person","Ansprechpartner"],
            ["phone","Telefon"],["mobile_phone","Mobilnummer"],["email","E-Mail"],
            ["address","Adresse"],["plz","PLZ"],["city","Stadt"],["country","Land (ISO-Code)"],
          ].map(([k,l]) => (
            <Field key={k} label={l} value={form[k]||""} onChange={(v)=>setForm({...form,[k]:v})} testid={`cust-${k}`}/>
          ))}
          <Field label="Notizen" value={form.notes||""} onChange={(v)=>setForm({...form,notes:v})} textarea colSpan={2}/>
        </ModalForm>
      )}
      <ConfirmDialog
        open={!!confirmState}
        title={confirmState?.type === "bulk" ? "Mehrere Kunden löschen?" : "Kunde löschen?"}
        message={confirmState?.type === "bulk"
          ? `${confirmState?.count} Kunden werden unwiderruflich gelöscht. Kunden mit verknüpften Rechnungen oder Aufträgen werden übersprungen. Fortfahren?`
          : `Kunde „${confirmState?.customer?.company_name}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`}
        confirmLabel={confirmState?.type === "bulk" ? `Ja, ${confirmState?.count} löschen` : "Ja, löschen"}
        tone="danger"
        busy={confirmBusy}
        onConfirm={confirmRemove}
        onCancel={() => !confirmBusy && setConfirmState(null)}
        testid="kunden-confirm-delete"
      />
    </div>
  );
}

/* ---------- Aufträge ---------- */
const EMPTY_ORDER = { number: "", customer_id: "", date: new Date().toISOString().slice(0,10), description: "", net_amount: 0, vat_rate: 20, status: "offen", notes: "" };
function Auftraege() {
  const { can } = usePermissions();
  const canCreate = can("finanzen", "create");
  const canEdit = can("finanzen", "edit");
  const canDelete = can("finanzen", "delete");
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_ORDER);

  // Phase 5Z.25 — bulk-select + confirm
  const bulk = useBulkSelect(items);
  const [confirmState, setConfirmState] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);

  async function load() { const { data } = await api.get("/orders", { params: { q, status: statusFilter } }); setItems(data); }
  useEffect(() => { api.get("/customers").then(({data}) => setCustomers(data)); }, []);
  useEffect(() => { load(); }, [q, statusFilter]);

  const custName = (id) => customers.find(c => c.id === id)?.company_name || "—";
  async function save() {
    try {
      const payload = { ...form, net_amount: +form.net_amount||0, vat_rate: +form.vat_rate||0 };
      if (editing) await api.put(`/orders/${editing.id}`, payload);
      else await api.post("/orders", payload);
      toast.success("Gespeichert"); setShowForm(false); setEditing(null); setForm(EMPTY_ORDER); load();
    } catch { toast.error("Fehler"); }
  }
  function remove(o) { setConfirmState({ type: "single", order: o }); }
  async function confirmRemove() {
    if (!confirmState) return;
    setConfirmBusy(true);
    try {
      if (confirmState.type === "single") {
        await api.delete(`/orders/${confirmState.order.id}`);
        toast.success("Auftrag gelöscht");
      } else {
        const ids = bulk.selectedIds;
        const results = await Promise.allSettled(ids.map((id) => api.delete(`/orders/${id}`)));
        const ok = results.filter((r) => r.status === "fulfilled").length;
        const fail = results.length - ok;
        if (ok) toast.success(`${ok} Auftrag${ok === 1 ? "" : "s"} gelöscht`);
        if (fail) toast.error(`${fail} fehlgeschlagen`);
        bulk.clear();
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen"); }
    finally { setConfirmBusy(false); setConfirmState(null); }
  }

  return (
    <div>
      <div className="flex justify-end mb-4">
        {canCreate && (
          <button data-testid="add-order-btn" onClick={()=>{ setEditing(null); setForm(EMPTY_ORDER); setShowForm(true); }} className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-md">
            <Plus className="w-4 h-4"/> Auftrag
          </button>
        )}
      </div>
      <BulkActionBar
        count={bulk.selectedCount}
        onClear={bulk.clear}
        onDelete={() => canDelete && setConfirmState({ type: "bulk", count: bulk.selectedCount })}
        deleteLabel={`${bulk.selectedCount} löschen`}
        testid="orders-bulk-bar"
      />
      <div className="bg-zinc-900 border border-zinc-800 rounded-md">
        <div className="p-4 flex gap-3 border-b border-zinc-800 flex-wrap">
          <div className="relative flex-1 min-w-[200px]"><Search className="absolute left-3 top-2.5 w-4 h-4 text-zinc-500"/>
            <input placeholder="Nummer, Beschreibung..." value={q} onChange={(e)=>setQ(e.target.value)} className="w-full pl-9 pr-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/></div>
          <select value={statusFilter} onChange={(e)=>setStatusFilter(e.target.value)} className="px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100">
            <option value="">Alle Status</option>
            {["offen","bestätigt","ausgeliefert","abgeschlossen","storniert"].map(s=><option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        {items.length === 0 ? <EmptyState title="Keine Aufträge"/> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
                <th className="px-4 py-3 text-left w-8"><BulkCheckbox checked={bulk.allSelected} indeterminate={bulk.someSelected} onChange={bulk.toggleAll} disabled={!canDelete} testid="orders-select-all" /></th>
                <th className="px-4 py-3 text-left">Nummer</th><th className="px-4 py-3 text-left">Datum</th>
                <th className="px-4 py-3 text-left">Kunde</th><th className="px-4 py-3 text-left">Beschreibung</th>
                <th className="px-4 py-3 text-right">Netto</th><th className="px-4 py-3 text-right">Brutto</th>
                <th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Aktionen</th>
              </tr></thead>
              <tbody>{items.map(o => (
                <tr key={o.id} className="border-b border-zinc-800 hover:bg-zinc-800/40">
                  <td className="px-4 py-2.5 w-8"><BulkCheckbox checked={bulk.isSelected(o.id)} onChange={() => bulk.toggle(o.id)} disabled={!canDelete} testid={`orders-select-${o.id}`} /></td>
                  <td className="px-4 py-2.5 font-mono text-xs text-zinc-100">{o.number}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-zinc-400">{fmtDate(o.date)}</td>
                  <td className="px-4 py-2.5 text-zinc-100">{custName(o.customer_id)}</td>
                  <td className="px-4 py-2.5 text-zinc-300 truncate max-w-[240px]">{o.description}</td>
                  <td className="px-4 py-2.5 text-right font-mono tabular-nums text-zinc-300">{fmtCur(o.net_amount)}</td>
                  <td className="px-4 py-2.5 text-right font-mono tabular-nums text-zinc-100">{fmtCur(o.gross_amount)}</td>
                  <td className="px-4 py-2.5"><StatusBadge status={o.status}/></td>
                  <td className="px-4 py-2.5 text-right">
                    {canEdit && <button onClick={()=>{ setEditing(o); setForm({...EMPTY_ORDER,...o}); setShowForm(true); }} className="p-1.5 text-zinc-400 hover:text-blue-400"><Pencil className="w-3.5 h-3.5"/></button>}
                    {canDelete && <button onClick={()=>remove(o)} className="p-1.5 text-zinc-400 hover:text-red-400 ml-1"><Trash2 className="w-3.5 h-3.5"/></button>}
                    {!canEdit && !canDelete && <span className="text-zinc-600 text-xs">—</span>}
                  </td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </div>
      {showForm && (
        <ModalForm title={editing?"Auftrag bearbeiten":"Neuer Auftrag"} onClose={()=>setShowForm(false)} onSave={save} saveTestId="order-save-btn">
          <Field label="Nummer (auto)" value={form.number||""} onChange={(v)=>setForm({...form,number:v})}/>
          <Field label="Datum" type="date" value={form.date||""} onChange={(v)=>setForm({...form,date:v})}/>
          <SelectField label="Kunde *" value={form.customer_id} onChange={(v)=>setForm({...form,customer_id:v})} options={[["",""],...customers.map(c=>[c.id,c.company_name])]} colSpan={2}/>
          <Field label="Beschreibung *" value={form.description} onChange={(v)=>setForm({...form,description:v})} colSpan={2}/>
          <Field label="Netto €" type="number" value={form.net_amount} onChange={(v)=>setForm({...form,net_amount:v})}/>
          <Field label="MwSt. %" type="number" value={form.vat_rate} onChange={(v)=>setForm({...form,vat_rate:v})}/>
          <SelectField label="Status" value={form.status} onChange={(v)=>setForm({...form,status:v})} options={[["offen","offen"],["bestätigt","bestätigt"],["ausgeliefert","ausgeliefert"],["abgeschlossen","abgeschlossen"],["storniert","storniert"]]}/>
          <Field label="Notizen" value={form.notes} onChange={(v)=>setForm({...form,notes:v})} textarea colSpan={2}/>
        </ModalForm>
      )}
      <ConfirmDialog
        open={!!confirmState}
        title={confirmState?.type === "bulk" ? "Mehrere Aufträge löschen?" : "Auftrag löschen?"}
        message={confirmState?.type === "bulk"
          ? `${confirmState?.count} Aufträge werden unwiderruflich gelöscht. Fortfahren?`
          : `Auftrag „${confirmState?.order?.number}" wirklich löschen?`}
        confirmLabel={confirmState?.type === "bulk" ? `Ja, ${confirmState?.count} löschen` : "Ja, löschen"}
        tone="danger"
        busy={confirmBusy}
        onConfirm={confirmRemove}
        onCancel={() => !confirmBusy && setConfirmState(null)}
        testid="orders-confirm-delete"
      />
    </div>
  );
}

/* ---------- Rechnungen / Gutschriften / Angebote ---------- */
function Rechnungen({ docType, title, prefix }) {
  const { can } = usePermissions();
  const canCreate = can("finanzen", "create");
  const canEdit = can("finanzen", "edit");
  const canDelete = can("finanzen", "delete");
  const canExport = can("finanzen", "export");
  const EMPTY_INV = { number: "", customer_id: "", order_id: "", firm_id: "", doc_type: docType, issue_date: new Date().toISOString().slice(0,10), due_date: "", sent_date: "", description: "", net_amount: 0, vat_rate: 20, paid_amount: 0, status: "entwurf", notes: "" };
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [firms, setFirms] = useState([]);
  const [q, setQ] = useState(""); const [statusFilter, setStatusFilter] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_INV);
  const [previewInv, setPreviewInv] = useState(null);
  const [sendInv, setSendInv] = useState(null);
  const [sp, setSp] = useSearchParams();

  // Phase 5Z.25 — bulk-select + confirm
  const bulk = useBulkSelect(items);
  const [confirmState, setConfirmState] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/invoices", { params: { q, status: statusFilter, doc_type: docType } });
      setItems(data);
    } finally { setLoading(false); }
  }
  useEffect(() => { api.get("/customers").then(({data}) => setCustomers(data)); }, []);
  useEffect(() => { api.get("/firms").then(({data}) => setFirms(data || [])).catch(() => setFirms([])); }, []);
  useEffect(() => { load(); setForm(f => ({ ...f, doc_type: docType })); }, [q, statusFilter, docType]);
  useEffect(() => {
    if (sp.get("new") === "1" && docType === "rechnung") {
      setEditing(null); setForm({ ...EMPTY_INV, doc_type: docType }); setShowForm(true);
      sp.delete("new"); setSp(sp, { replace: true });
    }
  }, [sp, docType]);  // eslint-disable-line

  const custName = (id) => customers.find(c => c.id === id)?.company_name || "—";

  async function save() {
    if (!form.customer_id) { toast.error("Bitte Kunden auswählen"); return; }
    if (!form.description?.trim()) { toast.error("Beschreibung ist Pflichtfeld"); return; }
    try {
      const payload = { ...form, doc_type: docType, net_amount: +form.net_amount||0, vat_rate: +form.vat_rate||0, paid_amount: +form.paid_amount||0 };
      if (editing) await api.put(`/invoices/${editing.id}`, payload);
      else await api.post("/invoices", payload);
      toast.success("Gespeichert"); setShowForm(false); setEditing(null); setForm(EMPTY_INV); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  }
  function remove(inv) { setConfirmState({ type: "single", inv }); }
  async function confirmRemove() {
    if (!confirmState) return;
    setConfirmBusy(true);
    try {
      if (confirmState.type === "single") {
        await api.delete(`/invoices/${confirmState.inv.id}`);
        toast.success("Gelöscht");
      } else {
        const ids = bulk.selectedIds;
        const results = await Promise.allSettled(ids.map((id) => api.delete(`/invoices/${id}`)));
        const ok = results.filter((r) => r.status === "fulfilled").length;
        const fail = results.length - ok;
        if (ok) toast.success(`${ok} Eintr${ok === 1 ? "ag" : "äge"} gelöscht`);
        if (fail) toast.error(`${fail} fehlgeschlagen`);
        bulk.clear();
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen"); }
    finally { setConfirmBusy(false); setConfirmState(null); }
  }
  async function markSent(id) {
    try { await api.post(`/invoices/${id}/send`); toast.success("Als gesendet markiert"); load(); } catch { toast.error("Fehler"); }
  }
  async function downloadPdf(inv) {
    try {
      const res = await api.get(`/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const labelMap = { rechnung: "Rechnung", gutschrift: "Gutschrift", angebot: "Angebot" };
      const label = labelMap[docType] || "Dokument";
      const a = document.createElement("a"); a.href = url; a.download = `${inv.number || label}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success(`${inv.number} heruntergeladen`);
    } catch { toast.error("PDF-Generierung fehlgeschlagen"); }
  }

  return (
    <div>
      <div className="flex justify-end mb-4">
        {canCreate && (
          <button data-testid={`add-${docType}-btn`} onClick={()=>{ setEditing(null); setForm({...EMPTY_INV, doc_type: docType}); setShowForm(true); }} className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-md">
            <Plus className="w-4 h-4"/> {title.slice(0,-2) || title}
          </button>
        )}
      </div>
      <BulkActionBar
        count={bulk.selectedCount}
        onClear={bulk.clear}
        onDelete={() => canDelete && setConfirmState({ type: "bulk", count: bulk.selectedCount })}
        deleteLabel={`${bulk.selectedCount} löschen`}
        testid={`${docType}-bulk-bar`}
      />
      <DataTable
        rows={items}
        loading={loading}
        searchTerm={q}
        onSearchChange={setQ}
        searchPlaceholder="Nummer, Beschreibung..."
        testid={`${docType}-table`}
        emptyTitle={`Keine ${title}`}
        emptyHint={q || statusFilter ? "Versuchen Sie andere Filter." : "Erstellen Sie den ersten Eintrag."}
        filters={
          <>
            <FirmFilter compact />
            <select value={statusFilter} onChange={(e)=>setStatusFilter(e.target.value)} data-testid={`${docType}-status-filter`}
              className="px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100">
              <option value="">Alle Status</option>
              {["entwurf","gesendet","bezahlt","überfällig","storniert","freigegeben"].map(s=><option key={s} value={s}>{s}</option>)}
            </select>
          </>
        }
        columns={[
          {
            key: "_select",
            label: <BulkCheckbox checked={bulk.allSelected} indeterminate={bulk.someSelected} onChange={bulk.toggleAll} disabled={!canDelete} testid={`${docType}-select-all`} />,
            headerClassName: "w-8",
            className: "w-8",
            render: (inv) => <BulkCheckbox checked={bulk.isSelected(inv.id)} onChange={() => bulk.toggle(inv.id)} disabled={!canDelete} testid={`${docType}-select-${inv.id}`} />,
          },
          { key: "number", label: "Nummer", sortable: true, render: (inv) => <span className="font-mono text-xs text-zinc-100">{inv.number}</span> },
          { key: "issue_date", label: "Datum", sortable: true, render: (inv) => <span className="font-mono text-xs text-zinc-400">{fmtDate(inv.issue_date)}</span> },
          { key: "due_date", label: "Fällig", sortable: true, render: (inv) => <span className={`font-mono text-xs ${inv.status === "überfällig" ? "text-red-400" : "text-zinc-400"}`}>{fmtDate(inv.due_date)}</span> },
          { key: "_customer", label: "Kunde", render: (inv) => <span className="text-zinc-100">{custName(inv.customer_id)}</span> },
          { key: "net_amount", label: "Netto", sortable: true,
            headerClassName: "text-right", className: "text-right font-mono tabular-nums text-zinc-300",
            render: (inv) => fmtCur(inv.net_amount) },
          { key: "gross_amount", label: "Brutto", sortable: true,
            headerClassName: "text-right", className: "text-right font-mono tabular-nums text-zinc-100",
            render: (inv) => fmtCur(inv.gross_amount) },
          { key: "paid_amount", label: "Bezahlt", sortable: true,
            headerClassName: "text-right", className: "text-right font-mono tabular-nums text-emerald-400",
            render: (inv) => fmtCur(inv.paid_amount) },
          { key: "status", label: "Status", sortable: true, render: (inv) => <StatusBadge status={inv.status} /> },
          {
            key: "_actions", label: "",
            headerClassName: "text-right", className: "text-right",
            render: (inv) => (
              <>
                <button onClick={() => setPreviewInv(inv)} title="Vorschau" data-testid={`inv-preview-${inv.id}`} className="p-1.5 text-zinc-400 hover:text-blue-400"><Eye className="w-3.5 h-3.5"/></button>
                {canExport && <button onClick={()=>downloadPdf(inv)} title="PDF herunterladen" data-testid={`inv-pdf-${inv.id}`} className="p-1.5 text-zinc-400 hover:text-blue-400 ml-1"><FileDown className="w-3.5 h-3.5"/></button>}
                {canEdit && <button onClick={()=>setSendInv(inv)} title="E-Mail mit Vorlage senden" data-testid={`inv-mail-${inv.id}`} className="p-1.5 text-zinc-400 hover:text-blue-400 ml-1"><Mail className="w-3.5 h-3.5"/></button>}
                {canEdit && !inv.sent_date && <button onClick={()=>markSent(inv.id)} title="Als gesendet markieren" className="p-1.5 text-zinc-400 hover:text-emerald-400 ml-1"><Send className="w-3.5 h-3.5"/></button>}
                {canEdit && <button onClick={()=>{ setEditing(inv); setForm({...EMPTY_INV,...inv}); setShowForm(true); }} title="Bearbeiten" className="p-1.5 text-zinc-400 hover:text-blue-400 ml-1"><Pencil className="w-3.5 h-3.5"/></button>}
                {canDelete && <button onClick={()=>remove(inv)} title="Löschen" className="p-1.5 text-zinc-400 hover:text-red-400 ml-1"><Trash2 className="w-3.5 h-3.5"/></button>}
              </>
            ),
          },
        ]}
      />
      {showForm && (
        <ModalForm title={editing?`${title.slice(0,-2) || title} bearbeiten`:`Neue${docType==="rechnung"?" Rechnung":docType==="gutschrift"?" Gutschrift":"s Angebot"}`} onClose={()=>setShowForm(false)} onSave={save} saveTestId={`${docType}-save-btn`}>
          <Field label={`Nummer (auto ${prefix}-YYYY-NNNN)`} value={form.number||""} onChange={(v)=>setForm({...form,number:v})}/>
          <SelectField label="Kunde *" value={form.customer_id} onChange={(v)=>setForm({...form,customer_id:v})} options={[["",""],...customers.map(c=>[c.id,c.company_name])]}/>
          {firms.length > 1 && (
            <SelectField label="Ausstellende Firma" value={form.firm_id||""} onChange={(v)=>setForm({...form,firm_id:v})} options={[["","— Auto (Standard) —"],...firms.map(f=>[f.id,f.name])]} testid="invoice-firm-select"/>
          )}
          <Field label="Ausstellungsdatum" type="date" value={form.issue_date||""} onChange={(v)=>setForm({...form,issue_date:v})}/>
          <Field label="Fälligkeitsdatum" type="date" value={form.due_date||""} onChange={(v)=>setForm({...form,due_date:v})}/>
          <Field label="Beschreibung *" value={form.description} onChange={(v)=>setForm({...form,description:v})} colSpan={2}/>
          <Field label="Netto €" type="number" value={form.net_amount} onChange={(v)=>setForm({...form,net_amount:v})}/>
          <Field label="MwSt. %" type="number" value={form.vat_rate} onChange={(v)=>setForm({...form,vat_rate:v})}/>
          <Field label="Bezahlt €" type="number" value={form.paid_amount} onChange={(v)=>setForm({...form,paid_amount:v})}/>
          <SelectField label="Status" value={form.status} onChange={(v)=>setForm({...form,status:v})} options={[["entwurf","entwurf"],["gesendet","gesendet"],["bezahlt","bezahlt"],["überfällig","überfällig"],["storniert","storniert"],["freigegeben","freigegeben"]]}/>
          <Field label="Notizen" value={form.notes} onChange={(v)=>setForm({...form,notes:v})} textarea colSpan={2}/>
          <div className="col-span-2 mt-2 p-3 bg-zinc-950 border border-zinc-800 rounded text-xs font-mono tabular-nums">
            <div className="flex justify-between text-zinc-400"><span>Netto</span><span>{fmtCur(+form.net_amount)}</span></div>
            <div className="flex justify-between text-zinc-400"><span>MwSt. ({+form.vat_rate||0}%)</span><span>{fmtCur((+form.net_amount||0) * (+form.vat_rate||0) / 100)}</span></div>
            <div className="flex justify-between text-zinc-100 font-semibold mt-1 pt-1 border-t border-zinc-800"><span>Brutto</span><span>{fmtCur((+form.net_amount||0) * (1 + (+form.vat_rate||0)/100))}</span></div>
          </div>
        </ModalForm>
      )}
      {previewInv && (
        <PdfPreviewModal
          api={api}
          url={`/invoices/${previewInv.id}/pdf`}
          filename={`${previewInv.number || "Rechnung"}.pdf`}
          title={`${previewInv.number || "Rechnung"} · ${custName(previewInv.customer_id)}`}
          onClose={() => setPreviewInv(null)}
        />
      )}
      <SendEmailDialog
        open={!!sendInv}
        onClose={() => setSendInv(null)}
        eventTrigger={docType === "angebot" ? "manual" : "invoice_send"}
        context={sendInv ? { invoice_id: sendInv.id, customer_id: sendInv.customer_id } : {}}
        relatedEntityType="invoice"
        relatedEntityId={sendInv?.id}
        title={sendInv ? `${sendInv.number} an ${custName(sendInv.customer_id)} senden` : "E-Mail senden"}
        onSent={() => {
          // mark as sent (sent_date) once successfully delivered
          if (sendInv && !sendInv.sent_date) {
            api.put(`/invoices/${sendInv.id}`, { ...sendInv, sent_date: new Date().toISOString().slice(0,10) }).then(load).catch(() => {});
          }
          setSendInv(null);
        }}
      />
      <ConfirmDialog
        open={!!confirmState}
        title={confirmState?.type === "bulk" ? `Mehrere ${title} löschen?` : "Eintrag löschen?"}
        message={confirmState?.type === "bulk"
          ? `${confirmState?.count} Einträge werden unwiderruflich gelöscht. Fortfahren?`
          : `${confirmState?.inv?.number || "Eintrag"} wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`}
        confirmLabel={confirmState?.type === "bulk" ? `Ja, ${confirmState?.count} löschen` : "Ja, löschen"}
        tone="danger"
        busy={confirmBusy}
        onConfirm={confirmRemove}
        onCancel={() => !confirmBusy && setConfirmState(null)}
        testid={`${docType}-confirm-delete`}
      />
    </div>
  );
}

/* ---------- Zahlungen ---------- */
function Zahlungen() {
  const { can } = usePermissions();
  const canCreate = can("finanzen", "create");
  const canDelete = can("finanzen", "delete");
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ invoice_id: "", date: new Date().toISOString().slice(0,10), amount: 0, method: "überweisung", reference: "", notes: "" });

  // Phase 5Z.25 — bulk select + confirm
  const bulk = useBulkSelect(payments);
  const [confirmState, setConfirmState] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);

  async function load() {
    const { data: inv } = await api.get("/invoices", { params: { doc_type: "rechnung" } });
    setInvoices(inv);
    const { data: p } = await api.get("/payments"); setPayments(p);
  }
  useEffect(() => { api.get("/customers").then(({data})=>setCustomers(data)); load(); }, []);
  const custName = (id) => customers.find(c => c.id === id)?.company_name || "—";
  const invNumber = (id) => invoices.find(i => i.id === id)?.number || "—";

  async function save() {
    try {
      await api.post("/payments", { ...form, amount: +form.amount||0 });
      toast.success("Zahlung erfasst");
      setShowForm(false); setForm({ invoice_id: "", date: new Date().toISOString().slice(0,10), amount: 0, method: "überweisung", reference: "", notes: "" }); load();
    } catch { toast.error("Fehler"); }
  }
  function remove(p) { setConfirmState({ type: "single", payment: p }); }
  async function confirmRemove() {
    if (!confirmState) return;
    setConfirmBusy(true);
    try {
      if (confirmState.type === "single") {
        await api.delete(`/payments/${confirmState.payment.id}`);
        toast.success("Zahlung gelöscht");
      } else {
        const ids = bulk.selectedIds;
        const results = await Promise.allSettled(ids.map((id) => api.delete(`/payments/${id}`)));
        const ok = results.filter((r) => r.status === "fulfilled").length;
        const fail = results.length - ok;
        if (ok) toast.success(`${ok} Zahlung${ok === 1 ? "" : "en"} gelöscht`);
        if (fail) toast.error(`${fail} fehlgeschlagen`);
        bulk.clear();
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen"); }
    finally { setConfirmBusy(false); setConfirmState(null); }
  }

  return (
    <div>
      <div className="flex justify-end mb-4">
        {canCreate && (
          <button data-testid="add-payment-btn" onClick={()=>setShowForm(true)} className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-md">
            <Plus className="w-4 h-4"/> Zahlung
          </button>
        )}
      </div>
      <BulkActionBar
        count={bulk.selectedCount}
        onClear={bulk.clear}
        onDelete={() => canDelete && setConfirmState({ type: "bulk", count: bulk.selectedCount })}
        deleteLabel={`${bulk.selectedCount} löschen`}
        testid="zahlungen-bulk-bar"
      />
      <div className="bg-zinc-900 border border-zinc-800 rounded-md">
        {payments.length === 0 ? <EmptyState title="Keine Zahlungen"/> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
                <th className="px-4 py-3 text-left w-8"><BulkCheckbox checked={bulk.allSelected} indeterminate={bulk.someSelected} onChange={bulk.toggleAll} disabled={!canDelete} testid="zahlungen-select-all" /></th>
                <th className="px-4 py-3 text-left">Datum</th><th className="px-4 py-3 text-left">Rechnung</th>
                <th className="px-4 py-3 text-left">Kunde</th><th className="px-4 py-3 text-right">Betrag</th>
                <th className="px-4 py-3 text-left">Methode</th><th className="px-4 py-3 text-left">Referenz</th>
                <th className="px-4 py-3 text-right">Aktion</th>
              </tr></thead>
              <tbody>{payments.map(p => {
                const inv = invoices.find(i => i.id === p.invoice_id);
                return (
                  <tr key={p.id} className="border-b border-zinc-800 hover:bg-zinc-800/40">
                    <td className="px-4 py-2.5 w-8"><BulkCheckbox checked={bulk.isSelected(p.id)} onChange={() => bulk.toggle(p.id)} disabled={!canDelete} testid={`zahlungen-select-${p.id}`} /></td>
                    <td className="px-4 py-2.5 font-mono text-xs text-zinc-400">{fmtDate(p.date)}</td>
                    <td className="px-4 py-2.5 font-mono text-xs text-zinc-100">{invNumber(p.invoice_id)}</td>
                    <td className="px-4 py-2.5 text-zinc-300">{inv ? custName(inv.customer_id) : "—"}</td>
                    <td className="px-4 py-2.5 text-right font-mono tabular-nums text-emerald-400">{fmtCur(p.amount)}</td>
                    <td className="px-4 py-2.5 text-zinc-300 capitalize">{p.method}</td>
                    <td className="px-4 py-2.5 font-mono text-xs text-zinc-400">{p.reference||"—"}</td>
                    <td className="px-4 py-2.5 text-right">{canDelete ? <button onClick={()=>remove(p)} className="p-1.5 text-zinc-400 hover:text-red-400"><Trash2 className="w-3.5 h-3.5"/></button> : <span className="text-zinc-600 text-xs">—</span>}</td>
                  </tr>
                );
              })}</tbody>
            </table>
          </div>
        )}
      </div>
      {showForm && (
        <ModalForm title="Neue Zahlung" onClose={()=>setShowForm(false)} onSave={save} saveTestId="payment-save-btn">
          <SelectField label="Rechnung *" value={form.invoice_id} onChange={(v)=>setForm({...form,invoice_id:v})} options={[["",""],...invoices.filter(i=>(i.paid_amount||0) < (i.gross_amount||0)).map(i=>[i.id, `${i.number} · ${custName(i.customer_id)} · offen ${fmtCur((i.gross_amount||0)-(i.paid_amount||0))}`])]} colSpan={2}/>
          <Field label="Datum" type="date" value={form.date} onChange={(v)=>setForm({...form,date:v})}/>
          <Field label="Betrag €" type="number" value={form.amount} onChange={(v)=>setForm({...form,amount:v})}/>
          <SelectField label="Methode" value={form.method} onChange={(v)=>setForm({...form,method:v})} options={[["überweisung","Überweisung"],["bar","Bar"],["karte","Karte"],["paypal","PayPal"]]}/>
          <Field label="Referenz" value={form.reference} onChange={(v)=>setForm({...form,reference:v})}/>
          <Field label="Notizen" value={form.notes} onChange={(v)=>setForm({...form,notes:v})} textarea colSpan={2}/>
        </ModalForm>
      )}
      <ConfirmDialog
        open={!!confirmState}
        title={confirmState?.type === "bulk" ? "Mehrere Zahlungen löschen?" : "Zahlung löschen?"}
        message={confirmState?.type === "bulk"
          ? `${confirmState?.count} Zahlungen werden unwiderruflich gelöscht. Fortfahren?`
          : `Zahlung über ${fmtCur(confirmState?.payment?.amount)} wirklich löschen?`}
        confirmLabel={confirmState?.type === "bulk" ? `Ja, ${confirmState?.count} löschen` : "Ja, löschen"}
        tone="danger"
        busy={confirmBusy}
        onConfirm={confirmRemove}
        onCancel={() => !confirmBusy && setConfirmState(null)}
        testid="zahlungen-confirm-delete"
      />
    </div>
  );
}

/* ---------- Shared form helpers ---------- */
function ModalForm({ title, children, onClose, onSave, saveTestId }) {
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
      <div className="bg-zinc-900 border border-zinc-800 rounded-md w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-zinc-800 flex items-center justify-between">
          <h3 className="font-display font-semibold text-zinc-50">{title}</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-100"><X className="w-5 h-5"/></button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-4">{children}</div>
        <div className="p-5 border-t border-zinc-800 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm bg-zinc-800 hover:bg-zinc-700 text-zinc-100 rounded">Abbrechen</button>
          <button onClick={onSave} data-testid={saveTestId} className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded font-medium">Speichern</button>
        </div>
      </div>
    </div>
  );
}
function Field({ label, value, onChange, type, textarea, colSpan = 1, testid }) {
  return (
    <div className={colSpan === 2 ? "col-span-2" : ""}>
      <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{label}</label>
      {textarea
        ? <textarea rows={2} value={value} onChange={(e)=>onChange(e.target.value)} className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 focus:ring-2 focus:ring-blue-600 focus:outline-none"/>
        : <input type={type||"text"} value={value} onChange={(e)=>onChange(e.target.value)} data-testid={testid}
                 className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 focus:ring-2 focus:ring-blue-600 focus:outline-none"/>
      }
    </div>
  );
}
function SelectField({ label, value, onChange, options, colSpan = 1 }) {
  return (
    <div className={colSpan === 2 ? "col-span-2" : ""}>
      <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{label}</label>
      <select value={value} onChange={(e)=>onChange(e.target.value)} className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}
